import { UserCog } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { useAccessUsers } from '@/features/access/api'
import { type RuleEvent, useRuleSlots } from './api'

/**
 * Kural motorunun görev rolleri için personel seçimi: her rol için seçilen kişi o roldeki görevlere atanır.
 * Rol tanımlı değilse hiçbir şey göstermez (görevler varsayılan sorumluya atanır).
 */
export function RoleAssignmentPicker({
  event,
  group,
  accountId,
  projectId,
  value,
  onChange,
}: {
  event: RuleEvent
  group?: string
  accountId?: string
  projectId?: string
  value: Record<string, string>
  onChange: (v: Record<string, string>) => void
}) {
  const { t } = useTranslation()
  const slots = useRuleSlots({ event, group, accountId, projectId })
  const users = useAccessUsers()
  const options = [{ value: 'none', label: t('projects.rules.assign.default') }, ...(users.data ?? []).map((u) => ({ value: u.id, label: u.name || u.email, description: u.position || u.department }))]
  if (!slots.data?.length) return null
  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
      <p className="flex items-center gap-2 text-sm font-medium">
        <UserCog className="size-4 text-primary" aria-hidden />
        {t('projects.rules.assign.title')}
      </p>
      <p className="text-xs text-muted-foreground">{t('projects.rules.assign.hint')}</p>
      {slots.data.map((slot) => (
        <div key={slot.role} className="grid grid-cols-1 gap-1.5 sm:grid-cols-[12rem_minmax(0,1fr)] sm:items-center">
          <Label htmlFor={`slot-${slot.role}`} title={slot.tasks.join(', ')}>
            {slot.role}
            <span className="block text-[11px] font-normal text-muted-foreground">{t('projects.rules.assign.taskCount', { count: slot.tasks.length })}</span>
          </Label>
          <Select
            id={`slot-${slot.role}`}
            searchable
            value={value[slot.role] || 'none'}
            options={options}
            onChange={(v) => {
              const next = { ...value }
              if (v === 'none') delete next[slot.role]
              else next[slot.role] = v
              onChange(next)
            }}
          />
        </div>
      ))}
    </div>
  )
}
